import React from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { HubShell } from '../../components/hub/shell';
import { useTileData } from '../../hooks/useTileData';
import { useActiveRole } from '../../hooks/useActiveRole';
import { useAuth } from '../../contexts/AuthContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { useHubAccess } from './access';
import { getClientContext, getClientEvidence, getClientNotes } from '../../api/hub';
import ClientDetailView from '../../components/hub/client/ClientDetailView';

const ClientDetailPageBody = () => {
  const { id } = useParams();
  const role = useActiveRole();
  const { loading: authLoading } = useAuth();
  // Workspace-based access (Epic 2 · S2.6): the wall lives in the database, not
  // in the legacy role number. A person who holds at least one ClientHub
  // workspace can open this page; a non-holder is sent to their own dashboard.
  const canHub = useHubAccess();
  const { loading: wsLoading } = useWorkspace();

  const context = useTileData(() => getClientContext(id), [id], !!id);
  const evidence = useTileData(() => getClientEvidence(id), [id], !!id);
  // Notes are fetched independently of context so a context failure never hides
  // the pinned observations that sit with the standing facts (spec §3.2).
  const notes = useTileData(() => getClientNotes(id), [id], !!id);

  if (authLoading || wsLoading) {
    return (
      <div className="bg-[#f7f8fa] dark:bg-gray-950 min-h-screen" role="status" aria-label="Loading">
        <span className="sr-only">Loading…</span>
      </div>
    );
  }
  if (!canHub) {
    return <Navigate to={role.isClient ? '/client-dashboard' : '/dashboard'} replace />;
  }

  return (
    <HubShell>
      <ClientDetailView id={id} context={context} evidence={evidence} notes={notes} />
    </HubShell>
  );
};

/**
 * The route element. Everything client-scoped (the three page-level reads and all state
 * below them) lives in the body and is KEYED by the client id, so moving from one client
 * to another remounts it: useTileData never clears its data when its dependencies change,
 * and an unkeyed page would show the previous client's name, contacts and pinned notes
 * until the new responses landed.
 */
const HubClientDetailPage = () => {
  const { id } = useParams();
  return <ClientDetailPageBody key={id} />;
};

export default HubClientDetailPage;
